﻿import { isbot } from 'isbot';

const scriptElement = document.querySelector<HTMLScriptElement>('script[data-applicationinsights]');

(async function () {
    if (isbot(navigator.userAgent)) {
        return;
    }

    if (typeof Array.prototype.toSorted !== 'function') {
        // we've had a lot of errors from some script kiddies running an old version of chrome
        // this will hopefully remove some noise from the logs. Array.toSorted was introduced:
        // Chrome 110	        Feb 2023
        // Firefox 115          Juli 2023
        // Safari 16.4	        Mars 2023
        // Edge (Chromium) 110	Feb 2023
        console.error('Browser to old to be logging stuff');
        return;
    }

    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');

    const connectionString = !scriptElement ? null : scriptElement.dataset.connectionstring;
    const applicationVersion = !scriptElement ? null : scriptElement.dataset.appVersion;
    const pageType = !scriptElement ? null : (scriptElement.dataset.pageType ?? null);

    if (!connectionString || !applicationVersion) {
        console.error('No connectionstring defined');
        return;
    }
    const biContextId =
        document.cookie
            .split('; ')
            .find((row) => row.startsWith('biContextId='))
            ?.split('=')[1] ?? null;

    if (
        !location.hostname.endsWith('bi.no') &&
        !location.hostname.endsWith('bi.edu') &&
        !location.hostname.endsWith('localhost')
    )
        return;

    const appInsights = new ApplicationInsights({
        config: {
            connectionString,
            disablePageUnloadEvents: ['unload'],
            extensionConfig: {
                ['AppInsightsCfgSyncPlugin']: {
                    cfgUrl: '', // this will block fetching from default cdn
                },
            },
        },
    });

    const errorsToIgnore = [
        'ErrorEvent: Script error.', // 3rd party js, details not available because of CORS
        "ReferenceError: Can't find variable: _AutofillCallbackHandler", // old instagram web view issue
        'Error', // noise
    ];

    appInsights.addTelemetryInitializer((envelope) => {
        switch (envelope.baseType) {
            case 'ExceptionData': {
                const isMessageToIgnore = envelope.data && errorsToIgnore.includes(envelope.data.message);
                const isFromBrowserExtension =
                    envelope.data && envelope.data.url && !envelope.data.url.startsWith('http');
                // Chrome iOS and the Google app inject a script that recurses through the DOM and can overflow
                // the stack. It shares WebKit with Safari, so a stack overflow in our own code still shows up there.
                const isInjectedIosChromiumStackOverflow =
                    /CriOS|GSA\//.test(navigator.userAgent) &&
                    envelope.data?.message?.startsWith('RangeError: Maximum call stack size exceeded');
                return !isMessageToIgnore && !isFromBrowserExtension && !isInjectedIosChromiumStackOverflow;
            }
            case 'RemoteDependencyData': {
                return !(
                    envelope.baseData &&
                    envelope.baseData.responseCode === 0 &&
                    envelope.baseData.target.includes('google-analytics.com')
                );
            }
            default:
                return true;
        }
    });

    // augment logs with context data
    appInsights.addTelemetryInitializer((envelope) => {
        envelope.data = envelope.data || {};
        envelope.data.applicationVersion = applicationVersion;
        envelope.data.biContextId = biContextId;
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    const { reportWebVitals } = await import('./webVitals');
    reportWebVitals(appInsights, pageType);
})();
